import {
  Button,
  Card,
  CardContent,
  IconButton,
  LinearProgress,
  Link,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import SoupKitchenIcon from '@mui/icons-material/SoupKitchen'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import { format, parse } from 'date-fns'
import { useState } from 'react'
import { Link as RouterLink, useNavigate, useSearchParams } from 'react-router-dom'
import { useOrders } from '@/api/hooks'
import { useAuthStore } from '@/auth/authStore'
import {
  EmptyState,
  ErrorBanner,
  formatDate,
  formatMoney,
  formatNumber,
  humanise,
  isoDate,
  PageHeader,
  StatusChip,
} from '@/components/common'

/** A `yyyy-MM-dd` filter value as the date-picker's own `Date`, or null if empty. */
function asDate(value: string) {
  return value ? parse(value, 'yyyy-MM-dd', new Date()) : null
}

/** A picked `Date` back to the `yyyy-MM-dd` string the filter is kept as. */
function asIsoDate(value: Date | null) {
  return value ? format(value, 'yyyy-MM-dd') : ''
}

const STATUSES = [
  'DRAFT',
  'AWAITING_ADVANCE',
  'CONFIRMED',
  'PLANNING',
  'READY_FOR_PRODUCTION',
  'IN_PROGRESS',
  'COMPLETED',
  'BILLED',
  'CLOSED',
  'CANCELLED',
]

export function OrdersPage() {
  const navigate = useNavigate()
  const canWrite = useAuthStore((state) => state.canWrite)
  // A report or another screen can send somebody here already looking for one
  // order number - a link that opened to today's window regardless would show
  // nothing for every order not on today's event date.
  const [searchParams] = useSearchParams()
  const linkedSearch = searchParams.get('q') ?? ''
  const [status, setStatus] = useState('')
  // Today, both ends, unless arriving already looking for something - then
  // every date is open, since the order being searched for was never
  // necessarily today's. The desk opens this page plain to see what is on
  // today; a window of four months opened it on a list nobody was looking
  // for, and the dates are there to be widened when somebody wants a wider
  // view.
  const [from, setFrom] = useState(() => (linkedSearch ? '' : isoDate()))
  const [to, setTo] = useState(() => (linkedSearch ? '' : isoDate()))
  const [q, setQ] = useState(linkedSearch)
  const [page, setPage] = useState(0)
  const [size, setSize] = useState(20)

  const { data, isLoading, error } = useOrders({
    status: status || undefined,
    from: from || undefined,
    to: to || undefined,
    q: q || undefined,
    page,
    size,
  })

  return (
    <>
      <PageHeader
        title="Orders"
        subtitle="Confirmed events from advance through to closure"
        actions={
          // For the customer who rings up and books: no quotation is sent, but
          // the priced record behind the order is still raised and kept.
          canWrite('order') ? (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => navigate('/orders/new')}
            >
              New order
            </Button>
          ) : null
        }
      />
      <ErrorBanner error={error} />

      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
            <TextField
              label="Search order number"
              value={q}
              onChange={(event) => {
                setQ(event.target.value)
                setPage(0)
              }}
              sx={{ minWidth: 220 }}
            />
            <TextField
              select
              label="Status"
              value={status}
              onChange={(event) => {
                setStatus(event.target.value)
                setPage(0)
              }}
              sx={{ minWidth: 200 }}
            >
              <MenuItem value="">All statuses</MenuItem>
              {STATUSES.map((value) => (
                <MenuItem key={value} value={value}>
                  {humanise(value)}
                </MenuItem>
              ))}
            </TextField>
            <DatePicker
              label="Event from"
              format="dd-MM-yyyy"
              value={asDate(from)}
              onChange={(value) => setFrom(asIsoDate(value))}
              slotProps={{ textField: { sx: { minWidth: 160 } } }}
            />
            <DatePicker
              label="Event to"
              format="dd-MM-yyyy"
              value={asDate(to)}
              onChange={(value) => setTo(asIsoDate(value))}
              slotProps={{ textField: { sx: { minWidth: 160 } } }}
            />
          </Stack>
        </CardContent>
      </Card>

      <Card>
        {isLoading && <LinearProgress />}
        {data && data.content.length === 0 ? (
          <EmptyState
            message="No orders in this window."
            hint="Orders appear here once a customer accepts a quotation and it is converted."
          />
        ) : (
          <>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Order</TableCell>
                  <TableCell>Customer</TableCell>
                  <TableCell>Event date</TableCell>
                  <TableCell>Service</TableCell>
                  <TableCell align="right">Guests</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Inv no</TableCell>
                  <TableCell align="right">Total</TableCell>
                  <TableCell align="right">Paid</TableCell>
                  <TableCell align="right">Balance</TableCell>
                  <TableCell />
                </TableRow>
              </TableHead>
              <TableBody>
                {data?.content.map((order) => (
                  // The whole row opens the order. Only the order number did,
                  // which is a small target in a wide row - every other list in
                  // the app opens on the row, and this one looked broken.
                  <TableRow
                    key={order.id}
                    hover
                    sx={{ cursor: 'pointer' }}
                    onClick={() => navigate(`/orders/${order.id}`)}
                  >
                    <TableCell>
                      <Link component={RouterLink} to={`/orders/${order.id}`} underline="hover">
                        {order.orderNumber}
                      </Link>
                    </TableCell>
                    <TableCell>{order.customerName}</TableCell>
                    <TableCell>{formatDate(order.eventDate)}</TableCell>
                    <TableCell>
                      <Typography variant="body2">{humanise(order.serviceType)}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {humanise(order.eventType)}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">{formatNumber(order.guestCount)}</TableCell>
                    <TableCell>
                      <StatusChip status={order.status} />
                    </TableCell>
                    <TableCell>{order.finalInvoiceNumber ?? '—'}</TableCell>
                    <TableCell align="right">{formatMoney(order.totalAmount)}</TableCell>
                    <TableCell align="right">{formatMoney(order.paidAmount)}</TableCell>
                    <TableCell align="right">
                      <Typography
                        variant="body2"
                        color={order.balanceAmount > 0 ? 'warning.main' : 'success.main'}
                      >
                        {formatMoney(order.balanceAmount)}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      {/* The kitchen's sheet, from the list. Somebody printing
                          the morning's dispatches is going down this list and
                          should not have to open each order to reach it.

                          The click is stopped: the row opens the order, and
                          this one thing on it does not. */}
                      <Tooltip title="Kitchen slip">
                        <IconButton
                          size="small"
                          aria-label={`Kitchen slip for ${order.orderNumber}`}
                          onClick={(event) => {
                            event.stopPropagation()
                            navigate(`/orders/${order.id}/kitchen-slip`)
                          }}
                        >
                          <SoupKitchenIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            {data && (
              <TablePagination
                component="div"
                count={data.totalElements}
                page={data.page}
                onPageChange={(_event, newPage) => setPage(newPage)}
                rowsPerPage={size}
                onRowsPerPageChange={(event) => {
                  setSize(Number(event.target.value))
                  setPage(0)
                }}
                rowsPerPageOptions={[10, 20, 50, 100]}
              />
            )}
          </>
        )}
      </Card>
    </>
  )
}
